import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { FormDatePicker } from "./DatePicker";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { ControllerRenderProps, FieldValues, Path, UseFormReturn } from "react-hook-form";

interface DateInputFieldProps extends FormFieldComponentProps {
  allowOlderDates?: boolean;
  showOptional?: boolean;
  /** Optional callback called when the date value changes */
  onChange?: () => void;
}

interface DatePickerControlProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>;
  form: UseFormReturn<T>;
  name: Path<T>;
  allowOlderDates?: boolean;
  disabled?: boolean;
  onChange?: () => void;
}

function DatePickerControl<T extends FieldValues>({
  field,
  form,
  name,
  allowOlderDates,
  disabled,
  onChange: customOnChange,
}: DatePickerControlProps<T>) {
  const { formMessageId, invalid } = useFormField();

  return (
    <FormDatePicker
      field={{
        ...field,
        onChange: (value) => {
          field.onChange(value);
          customOnChange?.();
        },
      }}
      allowOlderDates={allowOlderDates}
      disabled={disabled}
      errorDisplay="form"
      ariaDescribedBy={invalid ? formMessageId : undefined}
      onValidationError={(message) => {
        if (message) {
          form.setError(name, { type: "manual", message });
        } else {
          form.clearErrors(name);
        }
      }}
    />
  );
}

function DateInputField(props: DateInputFieldProps) {
  const {
    form,
    name,
    label,
    allowOlderDates,
    showOptional,
    className,
    disabled,
    onChange: customOnChange,
  } = props;

  const { t } = useTranslation(["common"]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col", className ?? "form-item")}>
          {label && (
            <FormLabel>
              {label}
              {showOptional && (
                <span className="text-slate-400 font-normal">
                  &nbsp;({t("optional")})
                </span>
              )}
            </FormLabel>
          )}
          <DatePickerControl
            field={field}
            form={form}
            name={name}
            allowOlderDates={allowOlderDates}
            disabled={disabled}
            onChange={customOnChange}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default DateInputField;
